import React from 'react';
import PropTypes from 'prop-types';
import { Table } from '@mantine/core';
import { getDateRangeLabel } from '../../../helpers/format';
import ReportTableColumn from './ReportTableColumn';

const isRenderSameAsBefore = (prevProps, nextProps) =>
  prevProps.showFirstColumnBorder === nextProps.showFirstColumnBorder &&
  prevProps.showSummary === nextProps.showSummary &&
  prevProps.pageIndex === nextProps.pageIndex &&
  prevProps.maxPageCount === nextProps.maxPageCount &&
  prevProps.data.length === nextProps.data.length &&
  prevProps.headers.length === nextProps.headers.length &&
  prevProps.headers.every((d, i) => nextProps.headers[i].field === d.field) &&
  !prevProps.compareData === !nextProps.compareData;

const summarizeData = (data, headers, isCompareData) => {
  const len = headers.length;

  const summary = new Array(len).fill(null);

  // Cache header info once (saves a lot of repeated object reads)
  const types = new Array(len);
  const fixed = new Array(len);

  // Only used for date columns
  const minDates = new Array(len).fill(null);
  const maxDates = new Array(len).fill(null);

  for (let i = 0; i < len; i++) {
    const h = headers[i];
    types[i] = h.dataType;

    const fixedValue =
      !isCompareData && h.averageValue != null
        ? Number(h.averageValue)
        : isCompareData && h.compareAverageValue != null
        ? Number(h.compareAverageValue)
        : null;

    fixed[i] = fixedValue;

    if (fixedValue != null) {
      summary[i] = fixedValue;
    }
    else if (types[i] === 'number' || types[i] === 'currency') {
      summary[i] = 0;
    }
  }

  // Summarize
  for (let r = 0; r < data.length; r++) {
    const row = data[r];

    for (let i = 0; i < len; i++) {
      if (fixed[i] != null) continue;

      const type = types[i];

      if (type === 'number' || type === 'currency') {
        summary[i] += Number(row[i]) || 0;
      }
      else if (type === 'date') {
        const d = row[i];
        if (!d) continue;

        // ISO string compare (fast + correct)
        if (minDates[i] == null || d < minDates[i]) minDates[i] = d;
        if (maxDates[i] == null || d > maxDates[i]) maxDates[i] = d;
      }
    }
  }

  // Finalize date columns
  for (let i = 0; i < len; i++) {
    if (types[i] === 'date') {
      const min = minDates[i];
      const max = maxDates[i];

      summary[i] = min && max ? getDateRangeLabel(min, max) : null;
    }
  }

  return summary;
};

const ReportTableBody = React.memo(
  ({
    data,
    compareData,
    headers,
    showFirstColumnBorder,
    showSummary,
    pageIndex,
    maxPageCount,
    onViewMore
  }) => {
    const showCompare = !!compareData && compareData.length > 0;

    const dataSummarized = React.useMemo(
      () => summarizeData(data, headers, false),
      [data, headers]
    );

    const compareDataSummarized = React.useMemo(
      () => (compareData ? summarizeData(compareData, headers, true) : null),
      [compareData, headers]
    );

    return (
      <Table.Tbody style={{ position: 'relative' }}>
        {showSummary && (
          <Table.Tr style={{ backgroundColor: '#F1F1F1' }}>
            {dataSummarized.map((c, i) => (
              <ReportTableColumn
                key={headers[i].field}
                compareValue={
                  compareDataSummarized ? compareDataSummarized[i] : null
                }
                header={headers[i]}
                index={i}
                isSummary
                onViewMore={onViewMore}
                showCompareValue={showCompare}
                showFirstColumnBorder={showFirstColumnBorder}
                value={c}
              />
            ))}
          </Table.Tr>
        )}

        {data.slice(0, pageIndex * maxPageCount).map((d, rowIndex) => {
          const compareDataRow = compareData ? compareData[rowIndex] : null;
          return (
            // eslint-disable-next-line react/no-array-index-key
            <Table.Tr key={`${d[0]}-${rowIndex}`}>
              {d.map((c, i) => (
                <ReportTableColumn
                  key={headers[i].field}
                  compareValue={compareDataRow && compareDataRow[i]}
                  header={headers[i]}
                  index={i}
                  onViewMore={(valueList) =>
                    onViewMore(headers[0], d[0], headers[i], valueList)
                  }
                  showCompareValue={showCompare}
                  showFirstColumnBorder={showFirstColumnBorder}
                  value={c}
                />
              ))}
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    );
  },
  isRenderSameAsBefore
);

ReportTableBody.propTypes = {
  compareData: PropTypes.array,
  data: PropTypes.array,
  headers: PropTypes.array,
  maxPageCount: PropTypes.number,
  onViewMore: PropTypes.func,
  pageIndex: PropTypes.number,
  showFirstColumnBorder: PropTypes.bool,
  showSummary: PropTypes.bool
};

export default ReportTableBody;
